import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Divider, Empty, Row, Select, Space, Statistic, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { OrganizationSalesDocument } from '../store/user/types';
import { getOrganizationDateParts } from '../services/dateTime';

const { Text, Title } = Typography;

type PeriodGroup = 'day' | 'month' | 'quarter' | 'year';
type SalesStatusFilter = 'TODOS' | 'PAGADO' | 'PENDIENTE' | 'ANULADO';

interface PlanBillingMetricsProps {
    getSalesDocuments: () => Promise<OrganizationSalesDocument[]>;
    getPendingPaymentRequestInvoices: () => Promise<PaymentRequest[]>;
}

interface MetricRow {
    label: string;
    value: number;
    amount: number;
}

interface PaymentRequest {
    id: string;
    organizationId: string;
    organizationName: string;
    clientName: string;
    planName: string;
    status: string;
    invoiceStatus: string;
    currency: string;
    total: number;
    paidAt?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface MetricDocument {
    id?: string;
    organizationId?: string;
    organizationName?: string | null;
    clientName?: string | null;
    planName?: string | null;
    status?: string | null;
    currency?: string | null;
    total?: number;
    paymentDate?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    paymentMethods?: Array<{ name?: string; amount?: number }>;
}

const chartColors = ['#2177f5', '#19b37b', '#f5a623', '#ef4444', '#7c3aed', '#0ea5e9', '#64748b', '#f97316'];

const money = (value: number, currency = 'USD') =>
    new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    }).format(value || 0);

const resolveDate = (document: MetricDocument) =>
    getOrganizationDateParts(document.paymentDate || document.createdAt || document.updatedAt);

const getPeriodLabel = (date: ReturnType<typeof getOrganizationDateParts>, group: PeriodGroup) => {
    const { year, month, day } = date;

    if (group === 'day') {
        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }

    if (group === 'month') {
        return new Intl.DateTimeFormat('es-EC', {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC'
        }).format(new Date(Date.UTC(year, month - 1, 1)));
    }

    if (group === 'quarter') {
        return `T${Math.ceil(month / 3)} ${year}`;
    }

    return String(year);
};

const getPeriodKey = (date: ReturnType<typeof getOrganizationDateParts>, group: PeriodGroup) => {
    const { year, month, day } = date;

    if (group === 'day') {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    if (group === 'month') {
        return `${year}-${String(month).padStart(2, '0')}`;
    }

    if (group === 'quarter') {
        return `${year}-Q${Math.ceil(month / 3)}`;
    }

    return String(year);
};

const getStatusColor = (status?: string | null) => {
    if (status === 'PAGADO') {
        return 'green';
    }

    if (status === 'PENDIENTE') {
        return 'gold';
    }

    if (status === 'ANULADO') {
        return 'red';
    }

    return 'default';
};

const sortByAmountDesc = (items: MetricRow[]) => [...items].sort((a, b) => b.amount - a.amount);
const sortByCountDesc = (items: MetricRow[]) => [...items].sort((a, b) => b.value - a.value);

const getOrganizationLabel = (document: MetricDocument) =>
    document.organizationName || document.clientName || document.organizationId || 'Sin organizacion';

const toMetricRows = (
    documents: MetricDocument[],
    getLabel: (document: MetricDocument) => string
) => {
    const map = new Map<string, MetricRow>();

    documents.forEach(document => {
        const label = getLabel(document);
        const current = map.get(label) || { label, value: 0, amount: 0 };
        current.value += 1;
        current.amount += Number(document.total || 0);
        map.set(label, current);
    });

    return Array.from(map.values());
};

const BarList: React.FC<{ rows: MetricRow[]; emptyText: string; showCount?: boolean }> = ({ rows, emptyText, showCount = true }) => {
    const maxAmount = rows.reduce((max, row) => Math.max(max, row.amount), 0);

    if (rows.length === 0) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
    }

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {rows.map(row => {
                const width = maxAmount > 0 ? Math.max((row.amount / maxAmount) * 100, 4) : 4;
                return (
                    <div className="tbc-metric-bar-row" key={row.label}>
                        <div className="tbc-metric-bar-heading">
                            <Text strong>{row.label}</Text>
                            <Text>{money(row.amount)}</Text>
                        </div>
                        <div className="tbc-metric-bar-track">
                            <div className="tbc-metric-bar-fill" style={{ width: `${width}%` }} />
                        </div>
                        {showCount && <Text type="secondary">{row.value} factura{row.value === 1 ? '' : 's'}</Text>}
                    </div>
                );
            })}
        </Space>
    );
};

const shortenLabel = (value: string, maxLength = 16) =>
    value.length > maxLength ? `${value.slice(0, maxLength - 1)}.` : value;

const ColumnChart: React.FC<{ rows: MetricRow[]; emptyText: string }> = ({ rows, emptyText }) => {
    const chartRows = rows.slice(-12);

    if (chartRows.length === 0 || chartRows.every(row => row.amount <= 0)) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
    }

    const data = chartRows.map(row => ({
        ...row,
        shortLabel: shortenLabel(row.label, 11)
    }));

    return (
        <div className="tbc-rechart-box">
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 18 }}>
                    <CartesianGrid strokeDasharray="4 5" vertical={false} />
                    <XAxis dataKey="shortLabel" tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={(value) => money(Number(value)).replace(',00', '')} tickLine={false} axisLine={false} width={72} />
                    <Tooltip
                        formatter={(value) => [money(Number(value || 0)), 'Facturacion']}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ''}
                    />
                    <Bar dataKey="amount" radius={[8, 8, 0, 0]} fill="#2177f5" name="Facturacion" />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const DonutChart: React.FC<{ rows: MetricRow[]; emptyText: string }> = ({ rows, emptyText }) => {
    const chartRows = sortByAmountDesc(rows).filter(row => row.amount > 0).slice(0, 6);
    const total = chartRows.reduce((sum, row) => sum + row.amount, 0);

    if (chartRows.length === 0 || total <= 0) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
    }

    return (
        <div className="tbc-donut-chart-wrap">
            <div className="tbc-rechart-donut">
                <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                        <Pie
                            data={chartRows}
                            dataKey="amount"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={88}
                            paddingAngle={3}
                        >
                            {chartRows.map((row, index) => (
                                <Cell key={row.label} fill={chartColors[index % chartColors.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => [money(Number(value || 0)), 'Monto']} />
                        <Legend verticalAlign="bottom" height={32} formatter={(value) => shortenLabel(String(value), 18)} />
                    </PieChart>
                </ResponsiveContainer>
                <div className="tbc-donut-center-label">
                    <Text strong>{money(total).replace(',00', '')}</Text>
                    <Text type="secondary">Total</Text>
                </div>
            </div>
            <Space direction="vertical" size={8} className="tbc-chart-legend">
                {chartRows.map((row, index) => (
                    <div className="tbc-chart-legend-row" key={row.label}>
                        <span className="tbc-chart-legend-dot" style={{ background: chartColors[index % chartColors.length] }} />
                        <Text>{shortenLabel(row.label, 22)}</Text>
                        <Text strong>{Math.round((row.amount / total) * 100)}%</Text>
                    </div>
                ))}
            </Space>
        </div>
    );
};

const PlanBillingMetrics: React.FC<PlanBillingMetricsProps> = ({ getSalesDocuments, getPendingPaymentRequestInvoices }) => {
    const [documents, setDocuments] = useState<MetricDocument[]>([]);
    const [periodGroup, setPeriodGroup] = useState<PeriodGroup>('month');
    const [statusFilter, setStatusFilter] = useState<SalesStatusFilter>('PAGADO');
    const [organizationFilter, setOrganizationFilter] = useState<string>('TODOS');

    const loadDocuments = async () => {
        const [salesDocumentsResponse, pendingPayPhoneResponse] = await Promise.all([
            getSalesDocuments(),
            getPendingPaymentRequestInvoices()
        ]);
        const payPhoneDocuments: MetricDocument[] = (pendingPayPhoneResponse || []).map(request => ({
            id: request.id,
            organizationId: request.organizationId,
            organizationName: request.organizationName,
            clientName: request.clientName,
            planName: request.planName || 'Sin plan',
            status: 'PAGADO',
            currency: request.currency,
            total: Number(request.total || 0),
            paymentDate: request.paidAt || request.updatedAt || request.createdAt,
            createdAt: request.createdAt,
            updatedAt: request.updatedAt,
            paymentMethods: [{ name: 'PayPhone', amount: Number(request.total || 0) }]
        }));

        setDocuments([...(salesDocumentsResponse || []), ...payPhoneDocuments]);
    };

    useEffect(() => {
        loadDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredDocuments = useMemo(() => {
        return documents.filter(document =>
            (statusFilter === 'TODOS' || document.status === statusFilter)
            && (organizationFilter === 'TODOS' || document.organizationId === organizationFilter)
        );
    }, [documents, statusFilter, organizationFilter]);

    const organizationOptions = useMemo(() => {
        const map = new Map<string, string>();
        documents.forEach(document => {
            if (document.organizationId) {
                map.set(document.organizationId, getOrganizationLabel(document));
            }
        });

        return Array.from(map.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, label]) => ({ value, label }));
    }, [documents]);

    const paidDocuments = useMemo(() => documents.filter(document => document.status === 'PAGADO'), [documents]);
    const pendingDocuments = useMemo(() => documents.filter(document => document.status === 'PENDIENTE'), [documents]);
    const annulledDocuments = useMemo(() => documents.filter(document => document.status === 'ANULADO'), [documents]);

    const totalPaid = paidDocuments.reduce((total, document) => total + Number(document.total || 0), 0);
    const totalPending = pendingDocuments.reduce((total, document) => total + Number(document.total || 0), 0);
    const totalAnnulled = annulledDocuments.reduce((total, document) => total + Number(document.total || 0), 0);

    const periodRows = useMemo(() => {
        const rows = toMetricRows(filteredDocuments, document => getPeriodLabel(resolveDate(document), periodGroup));
        return rows.sort((a, b) => {
            const first = filteredDocuments.find(document => getPeriodLabel(resolveDate(document), periodGroup) === a.label);
            const second = filteredDocuments.find(document => getPeriodLabel(resolveDate(document), periodGroup) === b.label);
            return getPeriodKey(resolveDate(first || filteredDocuments[0]), periodGroup)
                .localeCompare(getPeriodKey(resolveDate(second || filteredDocuments[0]), periodGroup));
        });
    }, [filteredDocuments, periodGroup]);

    const planRows = useMemo(() => {
        return sortByAmountDesc(toMetricRows(filteredDocuments, document => document.planName || 'Sin plan')).slice(0, 6);
    }, [filteredDocuments]);

    const planSoldRows = useMemo(() => {
        return sortByCountDesc(toMetricRows(filteredDocuments, document => document.planName || 'Sin plan')).slice(0, 6);
    }, [filteredDocuments]);

    const topPlan = planSoldRows[0];

    const organizationRows = useMemo(() => {
        return sortByAmountDesc(toMetricRows(filteredDocuments, getOrganizationLabel)).slice(0, 8);
    }, [filteredDocuments]);

    const statusRows = useMemo(() => {
        return sortByAmountDesc(toMetricRows(documents, document => document.status || 'SIN ESTADO'));
    }, [documents]);

    const paymentMethodRows = useMemo(() => {
        const map = new Map<string, MetricRow>();

        filteredDocuments.forEach(document => {
            (document.paymentMethods || []).forEach(paymentMethod => {
                const label = paymentMethod.name || 'Sin metodo';
                const current = map.get(label) || { label, value: 0, amount: 0 };
                current.value += 1;
                current.amount += Number(paymentMethod.amount || 0);
                map.set(label, current);
            });
        });

        return sortByAmountDesc(Array.from(map.values()));
    }, [filteredDocuments]);

    return (
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
            <Card>
                <Row gutter={[12, 12]} align="top">
                    <Col xs={24} md={6}>
                        <Text strong>Periodo</Text>
                        <Select
                            value={periodGroup}
                            onChange={(value) => setPeriodGroup(value)}
                            options={[
                                { value: 'day', label: 'Dias' },
                                { value: 'month', label: 'Meses' },
                                { value: 'quarter', label: 'Trimestres' },
                                { value: 'year', label: 'Años' }
                            ]}
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Text strong>Estado de factura</Text>
                        <Select
                            value={statusFilter}
                            onChange={(value) => setStatusFilter(value)}
                            options={[
                                { value: 'TODOS', label: 'Todos' },
                                { value: 'PAGADO', label: 'Pagadas' },
                                { value: 'PENDIENTE', label: 'Pendientes' },
                                { value: 'ANULADO', label: 'Anuladas' }
                            ]}
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Text strong>Organizacion</Text>
                        <Select
                            showSearch
                            value={organizationFilter}
                            onChange={(value) => setOrganizationFilter(value)}
                            optionFilterProp="label"
                            options={[
                                { value: 'TODOS', label: 'Todas' },
                                ...organizationOptions
                            ]}
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Button icon={<ReloadOutlined />} block onClick={loadDocuments} style={{ marginTop: 28 }}>
                            Actualizar metricas
                        </Button>
                    </Col>
                    <Col xs={24} md={6}>
                        <div className="tbc-metrics-status">
                            <Text type="secondary">Facturas analizadas</Text>
                            <Title level={4}>{filteredDocuments.length}</Title>
                        </div>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[12, 12]}>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic title="Facturado" value={totalPaid} precision={2} prefix="$" />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic title="Pendiente" value={totalPending} precision={2} prefix="$" />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic title="Anulado" value={totalAnnulled} precision={2} prefix="$" />
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card>
                        <Statistic title="Plan mas vendido" value={topPlan?.label || 'Sin datos'} />
                        {topPlan && <Text type="secondary">{topPlan.value} factura{topPlan.value === 1 ? '' : 's'}</Text>}
                    </Card>
                </Col>
            </Row>

            <Row gutter={[12, 12]}>
                <Col xs={24} xl={14}>
                    <Card title="Facturacion por periodo">
                        <ColumnChart rows={periodRows} emptyText="No hay facturas para graficar." />
                        <Divider />
                        <BarList rows={periodRows} emptyText="No hay facturas para el periodo seleccionado." />
                    </Card>
                </Col>
                <Col xs={24} xl={10}>
                    <Card title="Facturacion por plan">
                        <DonutChart rows={planRows} emptyText="No hay informacion por plan para graficar." />
                        <Divider />
                        <BarList rows={planRows} emptyText="No hay informacion por plan." />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[12, 12]}>
                <Col xs={24} xl={14}>
                    <Card title="Facturacion por organizacion">
                        <ColumnChart rows={organizationRows} emptyText="No hay organizaciones para graficar." />
                        <Divider />
                        <BarList rows={organizationRows} emptyText="No hay informacion por organizacion." />
                    </Card>
                </Col>
                <Col xs={24} xl={10}>
                    <Card title="Planes mas vendidos">
                        <DonutChart rows={planSoldRows} emptyText="No hay planes vendidos para graficar." />
                        <Divider />
                        <BarList rows={planSoldRows} emptyText="No hay informacion de ventas por plan." />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[12, 12]}>
                <Col xs={24} xl={12}>
                    <Card title="Estado de cartera">
                        <DonutChart rows={statusRows} emptyText="No hay facturas para graficar por estado." />
                        <Divider />
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            {statusRows.map(row => (
                                <div className="tbc-metric-status-row" key={row.label}>
                                    <Tag color={getStatusColor(row.label)}>{row.label}</Tag>
                                    <Text strong>{money(row.amount)}</Text>
                                    <Text type="secondary">{row.value} factura{row.value === 1 ? '' : 's'}</Text>
                                </div>
                            ))}
                            {statusRows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay facturas registradas." />}
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} xl={12}>
                    <Card title="Metodos de pago">
                        <DonutChart rows={paymentMethodRows} emptyText="No hay pagos para graficar." />
                        <Divider />
                        <BarList rows={paymentMethodRows} emptyText="No hay pagos registrados para el filtro actual." />
                    </Card>
                </Col>
            </Row>
        </Space>
    );
};

export default PlanBillingMetrics;
